import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
interface AnswerData {
  tag: string;
  text: string;
  count: number;
  percent: number;
}
interface QuestionBreakdownProps {
  questionAnswers: Record<number, AnswerData[]>;
}
interface BreakdownTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: AnswerData;
  }>;
}
function BreakdownTooltip({
  active,
  payload
}: BreakdownTooltipProps) {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return <div className="rounded-lg border border-white/15 bg-[#0c1222]/95 backdrop-blur-md px-4 py-3 shadow-xl max-w-xs" data-id="element-41">
      <p className="text-sm font-medium text-white mb-1" data-id="element-42">{d.text}</p>
      <p className="text-xs text-white/60" data-id="element-43">
        Responses: <span className="text-white font-medium" data-id="element-44">{d.count}</span> (
        {d.percent.toFixed(1)}%)
      </p>
    </div>;
}
export function QuestionBreakdown({
  questionAnswers
}: QuestionBreakdownProps) {
  const [selectedQ, setSelectedQ] = useState(1);
  const data = questionAnswers[selectedQ] || [];
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const chartData = data.map((d, i) => ({
    ...d,
    shortLabel: String.fromCharCode(65 + i)
  }));
  return <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 flex flex-col" style={{
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)'
  }} data-id="element-45">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2" data-id="element-46">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-white/40" data-id="element-47">
          Answer Distribution
        </h3>
        <select value={selectedQ} onChange={e => setSelectedQ(Number(e.target.value))} className="bg-white/[0.06] border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white/80 outline-none focus:border-cyan-500/50 transition-colors cursor-pointer" aria-label="Select question" data-id="element-48">
          {Array.from({
          length: 12
        }, (_, i) => <option key={i + 1} value={i + 1} className="bg-[#0c1222] text-white" data-id="element-49">
                Question {i + 1}
              </option>)}
        </select>
      </div>

      <div className="w-full h-64 mb-3" data-id="element-50">
        <ResponsiveContainer width="100%" height="100%" data-id="element-51">
          <BarChart data={chartData} layout="vertical" margin={{
          top: 4,
          right: 8,
          bottom: 4,
          left: 4
        }} data-id="element-52">
            <XAxis type="number" tick={{
            fill: 'rgba(255,255,255,0.3)',
            fontSize: 11
          }} axisLine={false} tickLine={false} data-id="element-53" />
            <YAxis type="category" dataKey="shortLabel" tick={{
            fill: 'rgba(255,255,255,0.5)',
            fontSize: 12,
            fontWeight: 600
          }} axisLine={false} tickLine={false} width={24} data-id="element-54" />
            <Tooltip content={<BreakdownTooltip data-id="element-56" />} cursor={{
            fill: 'rgba(255,255,255,0.03)'
          }} data-id="element-55" />
            <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28} data-id="element-57">
              {chartData.map(entry => <Cell key={entry.tag} fill={entry.count === maxCount ? '#00d4ff' : 'rgba(255,255,255,0.15)'} fillOpacity={entry.count === maxCount ? 0.85 : 0.6} data-id="element-58" />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex flex-col gap-1.5 mt-auto" data-id="element-59">
        {data.map((d, i) => <div key={d.tag} className="flex items-center gap-2 text-xs" data-id="element-60">
            <span className="w-4 text-white/50 font-bold" data-id="element-61">
              {String.fromCharCode(65 + i)}
            </span>
            <span className="text-white/50 truncate flex-1" data-id="element-62">{d.text}</span>
            <span className="text-white/30 font-mono" data-id="element-63">
              {d.percent.toFixed(0)}%
            </span>
          </div>)}
      </div>
    </div>;
}